import React, { useCallback, useEffect, useRef } from 'react';
import { registerOverlayPortal } from '@puckeditor/core';
import styles from './ProposalRichTextBubbleEditor.module.css';

function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    });
  };
}

export default function BubbleMenuShell({
  children,
  shellRef,
  rememberSelection,
  onInteractionStart,
  onInteractionEnd,
}) {
  const localRef = useRef(null);
  const setRef = mergeRefs(localRef, shellRef);
  // Fin d'interaction en attente : appelée par le premier des trois chemins
  // possibles (relâchement dans la barre, en dehors, geste annulé), une seule
  // fois.
  const finishRef = useRef(null);

  useEffect(() => {
    const el = localRef.current;
    if (!el) return undefined;
    // disableDragOnFocus (défaut) ajoute pointerdown capture après focus enfant
    // → bloque flèches NumberInput / ColorInput dans le bubble menu
    return registerOverlayPortal(el, { disableDragOnFocus: false });
  }, []);

  /** Capture : lock menu + sélection avant focus NumberInput (onStep focus l'input avant increment). */
  const handlePointerDownCapture = useCallback(() => {
    onInteractionStart?.();
    rememberSelection?.();
    // Fin d'interaction guettée sur la fenêtre, pas seulement sur la barre :
    // un pointeur relâché en dehors (glissé hors du bouton) ou annulé ne
    // repassait jamais par ici, l'interaction restait « en cours » et la barre
    // restait affichée pour de bon.
    const finish = () => {
      if (finishRef.current !== finish) return;
      finishRef.current = null;
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      window.setTimeout(() => onInteractionEnd?.(), 150);
    };
    finishRef.current = finish;
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }, [rememberSelection, onInteractionStart, onInteractionEnd]);

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handlePointerUp = useCallback((e) => {
    // stopPropagation empêche l'événement d'atteindre la fenêtre : on ferme
    // l'interaction ici, le guetteur global ne servant qu'aux relâchements
    // hors de la barre.
    e.stopPropagation();
    finishRef.current?.();
  }, []);

  const handleClick = useCallback((e) => {
    // Ne pas stopper les clics sur les contrôles (Popover, etc.)
    if (e.target instanceof Element && e.target.closest('button, input, [role="dialog"], [data-portal]')) {
      return;
    }
    e.stopPropagation();
  }, []);

  return (
    <div
      ref={setRef}
      className={styles.bubbleMenuShell}
      data-puck-overlay-portal="true"
      onPointerDownCapture={handlePointerDownCapture}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}
